// Angular + external
import { Component, ElementRef, OnDestroy, ViewChild } from "@angular/core";
import { AutoComplete } from "@bfx/primeng/autocomplete";
import { TranslateService } from "@ngx-translate/core";
import { Observable, Subject, Subscription } from "rxjs";
/***********************/
/* BFX */
// models
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { BfxBlotterActionEvent } from "../bfx-base-blotter/bfx-base-blotter-contextmenu.service";
import { BfxDialogData } from "./models/bfx-dialog-data.model";
// component
import { BfxDialogBaseComponent } from "./bfx-dialog-base.component";
// services
import { LoggerService } from "@bfx/logger";
import { OrderXferPromiseService } from "shared/cubiq-services";
import { BfxConfigService } from "../../shared/auth-services/system-config/bfx-config.service";
import { BfxBlotterOrdersActionService } from "../bfx-blotter-orders/bfx-blotter-orders-action.service";
const logger = LoggerService.for(__dirname);

import { NativeDesktopSupport } from "../bfx-native-desktop/native-desktop-support";

@Component({
  selector: "bfx-dialog-order-accept-into",
  templateUrl: "./html/bfx-dialog-order-accept-into.html",
  standalone: false,
})
export class BfxDialogOrderAcceptIntoComponent extends BfxDialogBaseComponent implements OnDestroy {
  @ViewChild("acceptInto_autoComplete", { static: true })
  private _acceptInto_autoComplete: AutoComplete;
  /***************************************/
  /* DATA */
  public config: any = {
    debug: true,
  };

  public data = {
    dialogId: null,
    dialogEventSubject: new Subject<BfxDialogData>(),
    actionEvent: null,
    payload: null,
    dialogData: {
      show: false,
      width: 450,
      height: 120,
      title_text: "Accept Order(s) Into",
      message_template: "Accept # orders into trader group: ",
      message_text: null,
      message_text_ids: null,
      autocomplete_traderGroup: null,
      autocomplete_traderGroups: [],
      autocomplete_traderGroupsFiltered: [],
      autocomplete_traderGroupsDefaultOnly: false,
      autocomplete_comment_text: "Comment",
      autocomplete_comment_value: "",
      queryCallOrderRateSide: false,
    },
  };

  private configurationSubscription: Subscription;

  /**********************************************************************/
  /* CONSTRUCTOR  + LIFE CYCLE HOOKS */
  constructor(
    private _configService: BfxConfigService,
    private _orderXferService: OrderXferPromiseService,
    private _orderActionsService: BfxBlotterOrdersActionService,
    private _translationService: TranslateService,
    private _element: ElementRef,
  ) {
    super();
    this.subscribeToConfiguration();
  }

  ngAfterViewInit() {
    this.patchDialogInitDrag();
    this.patchDialogMoveOnTop();
  }

  ngOnDestroy() {
    this.configurationSubscription.unsubscribe();
  }

  private patchDialogInitDrag(): void {
    if (this._dialog["__bfx_initDrag_close_autocomplete"]) {
      return;
    }

    this._dialog["__bfx_initDrag_close_autocomplete"] = true;

    const oldFn = this._dialog.initDrag;

    this._dialog.initDrag = function () {
      this._acceptInto_autoComplete.hide();

      oldFn.call(this._dialog, arguments[0]);
    }.bind(this);
  }

  private patchDialogMoveOnTop(): void {
    // extend moveOnTop logic to any children z-Indexes
    if (this._dialog["__bfx_moveOnTop_zindex_patch"]) {
      return;
    }

    this._dialog["__bfx_moveOnTop_zindex_patch"] = true;
    const oldFn = this._dialog.moveOnTop;

    this._dialog.moveOnTop = () => {
      oldFn.call(this._dialog);

      // FIXME: NG8
      // this._acceptInto_autoComplete.panelEL.nativeElement.style.zIndex = String(++DomHandler.zindex);
    };
  }

  /**********************************************************************/
  /* INITIALIZATION */
  private subscribeToConfiguration(): void {
    this.configurationSubscription = this._configService.uiConfig$.subscribe((config) => {
      this.config.debug = config.general.debug;
    });
  }

  /**********************************************************************/
  /* ACCEPT INTO */
  public openDialog(dialogData: BfxDialogData): Observable<any> {
    this.data.dialogId = dialogData.dialogId;

    let acceptanceCount: number = null;
    let xferCount: number = null;
    let event: BfxBlotterActionEvent = dialogData.event;
    this.data.actionEvent = event;

    this.data.dialogData.autocomplete_traderGroups = dialogData.payload.traderGroups.map((tg) => {
      return { label: tg.groupCode, value: tg.groupId };
    });

    // Handle default trader account and reset form to default
    if (this.data.dialogData.autocomplete_traderGroups.length === 1) {
      this.data.dialogData.autocomplete_traderGroupsDefaultOnly = true;
      this.data.dialogData.autocomplete_traderGroup =
        this.data.dialogData.autocomplete_traderGroups[0];
    } else {
      this.data.dialogData.autocomplete_traderGroupsDefaultOnly = false;
      this.data.dialogData.autocomplete_traderGroup = null;
    }
    this.data.dialogData.autocomplete_comment_value = "";

    if (
      dialogData.event != null &&
      dialogData.event.itemState != null &&
      dialogData.event.itemState.queryCallOrderRateSide === true
    ) {
      this.data.dialogData.queryCallOrderRateSide = true;
    }

    dialogData.payload.requests.acceptInto.acceptance.forEach((orderCount) => {
      if (Array.isArray(orderCount)) {
        return (acceptanceCount = orderCount.length);
      }
    });

    dialogData.payload.requests.acceptInto.xfer.forEach((orderCount) => {
      if (Array.isArray(orderCount)) {
        return (xferCount = orderCount.length);
      }
    });

    if (acceptanceCount === 1 || xferCount === 1) {
      this.data.dialogData.message_text = "Accept Order into trader group: ";
    } else {
      this.data.dialogData.message_text = this.data.dialogData.message_template.replace(
        "#",
        `${acceptanceCount + xferCount}`,
      );
    }

    NativeDesktopSupport.prepareEmbeddedDialog(dialogData.body, this._dialog, this._element);

    this.data.dialogData.show = true;
    return this.data.dialogEventSubject.asObservable();
  }
  public closeDialog(): void {
    super.closeDialog();
    this.data.dialogData.show = false;
    // inform dialog service that dialog is closed and to cleanup
    this.notifyDialogClosed(this.data.dialogEventSubject, this.data.dialogId);
  }

  private executeAcceptInto(callOrderRateSide: CubiqTS.RateSideType = null): void {
    this._orderActionsService.executeAcceptInto(
      this.data.actionEvent,
      this.data.dialogData.autocomplete_traderGroup.value,
      this.data.dialogData.autocomplete_comment_value,
      callOrderRateSide,
    );

    this.closeDialog();
  }

  /* ACCEPT INTO- Actions */
  public actionAcceptInto_confirm(): void {
    this.executeAcceptInto();
  }

  public actionAcceptInto_confirm_topside(): void {
    this.executeAcceptInto(CubiqTS.RateSideType.BID);
  }

  public actionAcceptInto_confirm_downside(): void {
    this.executeAcceptInto(CubiqTS.RateSideType.OFFER);
  }

  public actionAcceptInto_close(): void {
    this.closeDialog();
  }

  public helperAcceptInto_searchTraderGroup(event): void {
    let filteredTraderGroups = [];
    let traderGroups = this.data.dialogData.autocomplete_traderGroups;

    for (let i = 0; i < traderGroups.length; i++) {
      let traderGroup = traderGroups[i];
      if (
        event.query.length === 0 ||
        traderGroup.label.toLowerCase().indexOf(event.query.toLowerCase()) == 0
      ) {
        filteredTraderGroups.push(traderGroup);
      }
    }
    this.data.dialogData.autocomplete_traderGroupsFiltered = filteredTraderGroups;
  }
}
